import { cva, type VariantProps } from 'class-variance-authority'
import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react'

import { cn } from '../cn'

// Button — the foundational action primitive (design/components/button.html).
// Exactly one `primary` per screen; everything else steps down to ghost/link.
// `destructive` only for irreversible actions, always behind confirmation.

const buttonVariants = cva(
  [
    'relative inline-flex items-center justify-center gap-[7px] whitespace-nowrap font-semibold',
    'cursor-pointer select-none',
    'transition-[background-color,box-shadow,color] duration-quick ease-vysto',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
    'disabled:cursor-not-allowed',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        primary:
          'bg-brand-blue text-white shadow-btn-pri hover:bg-[#1d4fd1] active:bg-[#1538b8] focus-visible:outline-brand-blue disabled:bg-field disabled:text-text-3 disabled:shadow-none',
        secondary:
          'border border-input-line bg-surface text-text hover:bg-hover active:bg-field focus-visible:outline-brand-blue disabled:bg-surface disabled:text-text-3',
        destructive:
          'bg-sens text-white shadow-[0_1px_2px_rgba(214,69,63,0.4),0_6px_16px_-7px_rgba(214,69,63,0.6)] hover:bg-[#b8332c] focus-visible:outline-sens disabled:bg-field disabled:text-text-3 disabled:shadow-none',
        ghost:
          'bg-transparent text-text-2 hover:bg-hover hover:text-text focus-visible:outline-brand-blue disabled:text-text-3',
        link: 'bg-transparent text-link hover:underline focus-visible:outline-brand-blue disabled:text-text-3 disabled:no-underline',
      },
      size: {
        sm: 'rounded-sm px-[10px] py-[6px] text-[11.5px] [&_svg]:size-[13px]',
        md: 'rounded-md px-[14px] py-[9px] text-[12.5px] [&_svg]:size-[14px]',
        lg: 'rounded-[11px] px-[18px] py-3 text-[13.5px] [&_svg]:size-4',
      },
      block: { true: 'w-full' },
    },
    compoundVariants: [{ variant: 'link', class: 'p-0 shadow-none' }],
    defaultVariants: { variant: 'primary', size: 'md' },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Replaces the label with a spinner; keeps the button's width, sets aria-busy. */
  loading?: boolean
  iconStart?: ReactNode
  iconEnd?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    block,
    loading = false,
    iconStart,
    iconEnd,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={Boolean(disabled) || loading}
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, size, block }), loading && 'cursor-wait', className)}
      {...rest}
    >
      {loading && (
        // Centre with a positioned wrapper, NOT translate-x/y on the spinner
        // itself: `animate-spin` sets `transform: rotate()`, which would override
        // a centring translate and make the spinner drift off-centre.
        <span aria-hidden className="absolute inset-0 grid place-items-center">
          <span className="size-[15px] animate-spin rounded-full border-2 border-current/30 border-t-current motion-reduce:animate-none" />
        </span>
      )}
      <span className={cn('inline-flex items-center gap-[7px]', loading && 'invisible')}>
        {iconStart}
        {children}
        {iconEnd}
      </span>
    </button>
  )
})
Button.displayName = 'Button'
